import CategoryScroller from '@/components/news/CategoryScroller';
import NewsCard from '@/components/news/NewsCard';
import NewsPagination from '@/components/news/NewsPagination';
import SidebarPopularNews from '@/components/news/SidebarPopularNews';
import { ApiService } from '@/services/api.services';
import { formatDateIndo } from '@/utils/formatDate';
import { motion } from 'framer-motion';
import { Search, SlidersHorizontal } from 'lucide-react';
import { useEffect, useState } from 'react';
import { BodySm } from '../ui/typography/Typography';

export default function NewsList({ perPage = 6 }) {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const [sortBy, setSortBy] = useState('date');
  const [page, setPage] = useState(1);

  const [newsData, setNewsData] = useState([]);
  const [meta, setMeta] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
  });

  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [openSort, setOpenSort] = useState(false);

  /* ================================
   * Scroll ke atas saat page berubah
   * ================================ */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  /* ================================
   * Fetch Categories
   * ================================ */
  useEffect(() => {
    async function fetchCategories() {
      setCategoriesLoading(true);
      try {
        const res = await ApiService.getNewsCategories();
        const data = res?.data || [];
        setCategories([{ name: 'All', slug: 'all' }, ...data]);
      } catch (err) {
        console.error(err);
      } finally {
        setCategoriesLoading(false);
      }
    }

    fetchCategories();
  }, []);

  /* ================================
   * Debounce Search
   * ================================ */
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query);
      setPage(1);
      setSelectedCategory('all');
      setSearchLoading(true);
      setMeta({ current_page: 1, last_page: 1, total: 0 });
    }, 500);

    return () => clearTimeout(handler);
  }, [query]);

  /* ================================
   * Fetch News
   * ================================ */
  useEffect(() => {
    async function fetchNews() {
      setLoading(true);

      try {
        const res = await ApiService.getAllNews({
          page,
          per_page: perPage,
          category: selectedCategory !== 'all' ? selectedCategory : '',
          q: debouncedQuery || '',
        });

        setNewsData(res?.data || []);
        setMeta(res?.meta || { current_page: 1, last_page: 1, total: 0 });
      } catch (err) {
        console.error(err);
        setNewsData([]);
        setMeta({ current_page: 1, last_page: 1, total: 0 });
      } finally {
        setLoading(false);
        setSearchLoading(false);
      }
    }

    fetchNews();
  }, [page, selectedCategory, debouncedQuery, perPage]);

  /* ================================
   * Sort Client-side
   * ================================ */
  const filtered = [...newsData].sort((a, b) => {
    if (sortBy === 'date') return new Date(b.created_at) - new Date(a.created_at);
    if (sortBy === 'popular') return (b.views || 0) - (a.views || 0);
    return 0;
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
      {/* ================= MAIN CONTENT ================= */}
      <div className="lg:col-span-2">
        {/* Filter + Search */}
        <div className="mb-8 space-y-4">
          {categoriesLoading ? (
            <div className="flex gap-3 overflow-x-auto py-2">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="w-32 h-8 bg-gray-200 rounded-full animate-pulse" />
              ))}
            </div>
          ) : (
            <CategoryScroller
              categories={categories}
              selected={selectedCategory}
              onSelect={(slug) => {
                setSelectedCategory(slug);
                setPage(1);
              }}
            />
          )}
          {categoriesLoading ? (
            <div className="flex items-center gap-3">
              {/* Search Skeleton */}
              <div className="flex-1 h-12 bg-gray-200 rounded-full animate-pulse" />

              {/* Sort Skeleton */}
              <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse" />
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 " />
                <input
                  type="search"
                  placeholder="Cari berita..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="
          w-full h-10 sm:h-12 pl-12 pr-4 rounded-full bg-white text-sm sm:text-base
          border border-gray-300
          focus:border-accent-500
          focus:outline-none
          focus:ring-2 focus:ring-accent-200
          transition
        "
                />
              </div>

              <div className="relative">
                <button
                  onClick={() => setOpenSort((p) => !p)}
                  className="w-12 h-12 rounded-full border bg-white shadow-sm flex items-center justify-center"
                >
                  <SlidersHorizontal className="w-5 h-5" />
                </button>

                {openSort && (
                  <div className="absolute right-0 mt-2 w-40 bg-white border rounded-xl shadow-lg z-50">
                    <button
                      onClick={() => setSortBy('date')}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100"
                    >
                      Terbaru
                    </button>
                    <button
                      onClick={() => setSortBy('popular')}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100"
                    >
                      Terpopuler
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ================= SEARCH INFO ================= */}
        {debouncedQuery && !searchLoading && meta.total > 0 && (
          <BodySm className="text-gray-500 mb-4">
            Menampilkan <span className="font-semibold">{meta.total}</span> berita untuk pencarian{' '}
            <span className="font-semibold">{debouncedQuery}</span>
          </BodySm>
        )}

        {/* ================= NEWS GRID ================= */}
        <motion.div
          key={`${selectedCategory}-${sortBy}-${page}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {[...Array(perPage)].map((_, i) => (
                <div key={i} className="h-80 bg-gray-200 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-gray-500">
              <BodySm>Tidak ada berita ditemukan.</BodySm>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {filtered.map((item) => (
                <NewsCard
                  key={item.id}
                  item={{
                    ...item,
                    date: formatDateIndo(item.created_at),
                    category: item.category?.name || 'Uncategorized',
                  }}
                />
              ))}
            </div>
          )}
        </motion.div>

        {/* ================= PAGINATION ================= */}
        {meta.last_page > 1 && (
          <div className="mt-10">
            <NewsPagination
              currentPage={meta.current_page}
              totalPages={meta.last_page}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* ================= SIDEBAR ================= */}
      <div className="lg:col-span-1">
        <SidebarPopularNews />
      </div>
    </div>
  );
}
